<template>
  <NuxtLayout>
    <div class="slot-body">
      <NavBarPages v-if="isMobile()" />
      <!-- Latest making-of: desktop + active user (top) -->
      <div class="block--gray">
        <LatestVideoPlayer
          v-if="latestVideo && !isMobile() && !isPreview"
          :data="latestVideo"
          contentType="making-of"
          :isPreview="false"
          variant="desktop"
        />
      </div>
      <h1 class="visually-hidden">{{ translate.title }}</h1>
      <div v-if="showBanner" @click="redirect('language-assine')" style="line-height: 0;">
        <video
          class="cursor-pointer"
          autoplay muted loop playsinline
          width="100%"
          height="100%"
          :src="route.params.language === 'pt-br' ?
                  (isMobile() ? BannerMakingOfMobileBr : BannerMakingOfDesktopBr) :
                  (isMobile() ? BannerMakingOfMobileEn : BannerMakingOfDesktopEn)"
        >
        </video>
      </div>
      <div v-if="!showBanner && !(latestVideo && !isMobile()) && (isPreview || isMobile())" class="container">
        <div class="row pb-5 pt-4 align-items-center">
          <div class="col-lg-3 mt-4">
            <div class="title">
              {{ translate.title }}
            </div>
          </div>
          <div class="col-lg-4">
            <span class="subtitle">
              {{ categorySubtitle }}
            </span>
          </div>
        </div>
      </div>
      <!-- Latest making-of: desktop + inactive/not logged (below banner) -->
      <div class="block--gray">
        <LatestVideoPlayer
          v-if="latestVideo && !isMobile() && isPreview"
          :data="latestVideo"
          contentType="making-of"
          :isPreview="true"
          variant="desktop"
        />
      </div>
      <div class="col-lg-12 search-desktop">
        <ModelSearch
          :options="options"
          @loadModel="mySelectEvent"
        />
      </div>
      <div class="block block--gray">
        <div class="container">
          <div class="search-mobile mb-3">
            <ModelSearch
              :options="options"
              @loadModel="mySelectEvent"
            />
          </div>
          <!-- Latest making-of: mobile (below search bar) -->
          <LatestVideoPlayer
            v-if="latestVideo && isMobile() && showPaginate"
            :data="latestVideo"
            contentType="making-of"
            :isPreview="isPreview"
            variant="mobile"
          />
          <AllModelsPAginationMakingOf
            v-if="showPaginate"
            :models="models"
            :apiEndpoint="`v1/modelos/making-of/categories/${route.params.slug}`"
            :categories="categories"
            :activeCategory="(route.params.slug as string)"
          />
          <ModelSingleCardMakingOf v-else :models="models" />
        </div>
      </div>

      <div class="pt-5 pb-5 z-1 crsl-left">
        <div class="title-models pb-5">
          {{ translate.featuredModels }}
        </div>
        <div class="col-lg-12">
          <CarroselTopPeccinMakingOf :posts="posts" />
        </div>
      </div>

      <div class="py-5">
        <BannerAssineHorizontal :path="bannerAssineHorizontal" />
      </div>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
// SEO: página de conteúdo explícito — marcação que o Google pede para não
// classificar o site inteiro como adulto. Ver composables/useAdultRating.ts
useAdultRating();
import makingOfTradutor from "@/tradutors/makingof";
import CarroselTopPeccinMakingOf from "@/components/Modelos/CarroselTopPeccinMakingOf.vue";
import AllModelsPAginationMakingOf from "@/components/Modelos/AllModelsPAginationMakingOf.vue";
import BannerAssineHorizontal from "@/components/HomePage/BannerAssineHorizontal.vue";
import ModelSingleCardMakingOf from "@/components/Modelos/ModelSingleCardMakingOf.vue";
import { isMobile } from "@/shared/utils/mobile";
import ModelSearch from "@/components/Modelos/ModelSearch.vue";
import api from "@/api";
import BannerMakingOfDesktopBr from "@/assets/img/banner-making-of-desktop-br.mp4";
import BannerMakingOfDesktopEn from "@/assets/img/banner-making-of-desktop-en.mp4";
import BannerMakingOfMobileBr from "@/assets/img/banner-making-of-mobile-br.mp4";
import BannerMakingOfMobileEn from "@/assets/img/banner-making-of-mobile-en.mp4";
import NavBarPages from "~/components/Modelos/NavBarPages.vue";
import LatestVideoPlayer from "@/components/LatestVideoPlayer.vue";

definePageMeta({
  middleware: ["not-found-lang"],
});

const route = useRoute();
const canonical = useCanonicalUrl();
const router = useRouter();
const translate = computed(() =>
  (route.params.language) == "pt-br"
    ? makingOfTradutor.portugues
    : makingOfTradutor.ingles
);

const storeUser = userStore();
const showBanner = computed(() => !storeUser.isActiveUser);


// SSR data fetch
const { data, error } = await useAsyncData(
  `category-makingof-${route.params.slug}-${route.params.language}`,
  async () => {
    try {
      const [categoryRes, categoriesRes, postsRes, bannerRes] = await Promise.all([
        api.get(
          `v1/modelos/making-of/categories/${route.params.slug}?language=${route.params.language}&order=order`
        ),
        api.get(`v1/modelos/making-of/categories?language=${route.params.language}`),
        api.get(`v1/modelos/getMakingOfModelsLastFive?language=${route.params.language}`),
        api.get(`v1/home/getBannerSuper?mobile=${isMobile()}&language=${route.params.language}`),
      ]);

      return {
        category: categoryRes.data.category,
        modelsArray: categoryRes.data.modelsArray,
        categories: categoriesRes.data.categories ?? [],
        postsCarroselModels: postsRes.data.postsCarroselModels,
        bannerAssineHorizontal: bannerRes.data.bannerAssineHorizontal,
      };
    } catch (err: any) {
      if (err?.response?.status === 404) {
        throw createError({ statusCode: 404, statusMessage: "Categoria nao encontrada" });
      }
      throw err;
    }
  }
);

if (error.value) {
  throw createError({ statusCode: 404, statusMessage: "Categoria nao encontrada" });
}

if (!data.value) {
  throw createError({ statusCode: 404, statusMessage: "Categoria nao encontrada" });
}

const category = ref(data.value.category);
const models = ref(data.value.modelsArray);
const categories = ref(data.value.categories);
const posts = ref(data.value.postsCarroselModels);
const bannerAssineHorizontal = ref(data.value.bannerAssineHorizontal);
const showPaginate = ref(true);
const options = ref([]);

const { latestVideo } = useLatestVideo('making-of');
const isPreview = computed(() => !storeUser.isActiveUser);

// Dynamic subtitle: use category description if available
const categorySubtitle = computed(() => {
  const desc = category.value?.description;
  return desc && desc.trim() ? desc : translate.value.subTitle;
});

// SEO meta
useHead({
  templateParams: {
    subTitle: null,
    subPage: category.value?.seoTitle || category.value?.name || translate.value.title,
    page_url: canonical,
    page_description: category.value?.seoDescription || "",
    page_image: shareImage(),
    page_keywords: category.value?.keywords || "",
    page_locale: route.params.language as string,
    page_url_pt: `https://belladasemana.com.br/pt-br/making-of/categoria/${route.params.slug}`,
    page_url_en: `https://belladasemana.com.br/en/making-of/categoria/${route.params.slug}`,
    page_canonical: canonical,
  },
});

onMounted(() => {
  getSelectModels();
});

async function getSelectModels() {
  api.get("v1/modelos/getMakingOfModelsSelect").then(({ data }) => {
    options.value = data.modelsArray;
  });
}

function mySelectEvent(val: number) {
  if (val === undefined) {
    // reset to category models
    api
      .get(
        `v1/modelos/making-of/categories/${route.params.slug}?language=${route.params.language}&order=order`
      )
      .then(({ data }) => {
        models.value = data.modelsArray;
        showPaginate.value = true;
      });
  } else {
    api
      .get(`v1/modelos/getModelSelect/${val}?language=${route.params.language}`)
      .then(({ data }) => {
        models.value = data.modelsArray;
        showPaginate.value = false;
      });
  }
}

function redirect(name: string) {
  router.push({ name, params: { language: route.params.language } });
}
</script>

<style scoped lang="css">
.slot-body {
  margin-top: 65px !important;
  background-color: #fff !important;
}

.title {
  font-family: "Bebas-Neue-Light";
  height: 77px;
  color: #000000;
  font-size: 64px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 77px;
}

.title-models {
  color: #b20000;
  font-family: "Roboto-Medium";
  font-size: 32px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 38px;
}

.subtitle {
  color: #5b5858;
  font-family: "Roboto-Medium";
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 28px;
}

.search-desktop {
  transform: translate(25%);
  z-index: 3;
  width: 340px;
  position: absolute;
  right: 190px;
  margin-top: 26px;
}

.search-mobile {
  display: none;
}

.crsl-left {
  margin-left: 90px;
}

.block {
  padding: 30px 0;
}

@media screen and (max-width: 992px) {
  .search-desktop {
    transform: unset;
  }
}

/* tablets e iPads: busca em fluxo como 1ª linha, acima da toolbar */
@media screen and (min-width: 770px) and (max-width: 1300px) {
  .search-desktop {
    display: none;
  }

  .search-mobile {
    display: block;
    z-index: 3;
    width: 50%;
    margin-left: auto;
    margin-right: auto;
  }
}

@media screen and (max-width: 769px) {
  .title-models {
    font-size: 24px;
    padding-bottom: 20px !important;
  }
  .search-desktop {
    display: none;
  }
  .search-mobile {
    display: block;
    z-index: 3;
  }

  .crsl-left {
    margin-left: 8px;
    padding-top: 20px !important;
  }

  .title {
    font-family: "Bebas-Neue-Light";
    color: #000000;
    font-size: 48px;
    font-weight: 300;
    letter-spacing: 0;
    line-height: 58px;
  }
  .z-2 {
    padding-top: 30px !important;
  }
  .subtitle {
    color: #5b5858;
    font-family: "Roboto-Medium";
    font-size: 16px;
    letter-spacing: 0;
    line-height: 19px;
  }
}
</style>
